import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Power, PowerOff } from "lucide-react";
import { authApi, formatApiError } from "../AuthContext";
import Modal, { ConfirmDialog } from "../Modal";

const ICONS = ["Target", "Activity", "CircleDot", "Crown", "Swords", "Dumbbell", "Shield", "Zap", "TrendingUp", "Goal", "Volleyball", "Waves", "Heart", "Sparkles", "Users"];

const empty = {
  slug: "", name: "", short_description: "", long_description: "",
  is_active: false, age_groups: [], training_days: [], coach_name: "",
  image_url: "", icon: "Dumbbell", order: 0,
};

export default function BranchesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    setLoading(true);
    authApi.get("/admin/branches")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (b) => {
    try {
      await authApi.put(`/admin/branches/${b.id}`, { is_active: !b.is_active });
      toast.success(`${b.name}: ${!b.is_active ? "Aktif" : "Pasif"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async () => {
    await authApi.delete(`/admin/branches/${confirmDel.id}`);
    toast.success("Branş silindi");
    load();
  };

  return (
    <div data-testid="admin-branches">
      <Header title="Branşlar" sub="Aktif/Pasif yap, yeni branş ekle, düzenle veya sil." onAdd={() => setEditing({ ...empty })} addLabel="Yeni Branş" />

      {loading ? (
        <div className="text-muted-foreground py-12 text-center">Yükleniyor...</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <div key={b.id} className="card-elev p-5" data-testid={`admin-branch-${b.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display text-xl font-bold uppercase tracking-tight">{b.name}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-1">/branslar/{b.slug}</div>
                </div>
                {b.is_active ? <span className="badge-status-active">Aktif</span> : <span className="badge-status-soon">Yakında</span>}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{b.short_description}</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => toggleActive(b)} className="btn-ghost text-xs px-2 py-1.5" data-testid={`branch-toggle-${b.slug}`}>
                  {b.is_active ? <><PowerOff className="h-3 w-3" /> Pasifleştir</> : <><Power className="h-3 w-3" /> Aktifleştir</>}
                </button>
                <button type="button" onClick={() => setEditing(b)} className="btn-ghost text-xs px-2 py-1.5" data-testid={`branch-edit-${b.slug}`}>
                  <Edit2 className="h-3 w-3" /> Düzenle
                </button>
                <button type="button" onClick={() => setConfirmDel(b)} className="btn-ghost text-xs px-2 py-1.5 text-destructive hover:text-destructive" data-testid={`branch-delete-${b.slug}`}>
                  <Trash2 className="h-3 w-3" /> Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BranchForm
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={onDelete}
        title="Branşı sil?"
        body={confirmDel ? `${confirmDel.name} branşı kalıcı olarak silinecek.` : ""}
      />
    </div>
  );
}

function BranchForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;
  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, age_groups: form.age_groups || [], training_days: form.training_days || [] };
      if (isNew) await authApi.post("/admin/branches", payload);
      else await authApi.put(`/admin/branches/${form.id}`, payload);
      toast.success(isNew ? "Branş eklendi" : "Branş güncellendi");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const csv = (val) => Array.isArray(val) ? val.join(", ") : "";
  const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <Modal open={true} onClose={onClose} title={isNew ? "Yeni Branş" : "Branşı Düzenle"} size="lg">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="branch-form">
        <Field label="Branş Adı" required><input className="input-base" required value={form.name} onChange={update("name")} data-testid="bf-name" /></Field>
        <Field label="Slug (URL)" required><input className="input-base" required value={form.slug} onChange={update("slug")} placeholder="ornegin: futbol" data-testid="bf-slug" /></Field>
        <Field label="İkon">
          <select className="input-base" value={form.icon} onChange={update("icon")} data-testid="bf-icon">
            {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Sıralama"><input type="number" className="input-base" value={form.order} onChange={update("order")} data-testid="bf-order" /></Field>
        <div className="sm:col-span-2"><Field label="Kısa Açıklama" required><textarea className="input-base min-h-[60px]" required value={form.short_description} onChange={update("short_description")} data-testid="bf-short" /></Field></div>
        <div className="sm:col-span-2"><Field label="Detay Açıklama"><textarea className="input-base min-h-[100px]" value={form.long_description || ""} onChange={update("long_description")} data-testid="bf-long" /></Field></div>
        <Field label="Yaş Grupları (virgülle ayır)"><input className="input-base" value={csv(form.age_groups)} onChange={(e) => setForm((f) => ({ ...f, age_groups: fromCsv(e.target.value) }))} placeholder="6-9, 10-14, 15-18" data-testid="bf-ages" /></Field>
        <Field label="Eğitim Günleri (virgülle ayır)"><input className="input-base" value={csv(form.training_days)} onChange={(e) => setForm((f) => ({ ...f, training_days: fromCsv(e.target.value) }))} placeholder="Pazartesi, Çarşamba, Cuma" data-testid="bf-days" /></Field>
        <Field label="Antrenör"><input className="input-base" value={form.coach_name || ""} onChange={update("coach_name")} data-testid="bf-coach" /></Field>
        <Field label="Görsel URL"><input className="input-base" value={form.image_url || ""} onChange={update("image_url")} data-testid="bf-image" /></Field>
        <div className="sm:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="h-4 w-4 accent-[hsl(202_100%_50%)]" checked={!!form.is_active} onChange={update("is_active")} data-testid="bf-active" />
            <span className="font-display text-sm font-bold uppercase tracking-wider">Aktif Branş Olarak Yayınla</span>
          </label>
        </div>
        <div className="sm:col-span-2 flex justify-end gap-3 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-secondary">İptal</button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="bf-submit">
            {saving ? "Kaydediliyor..." : (isNew ? "Branş Ekle" : "Değişiklikleri Kaydet")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}

function Header({ title, sub, onAdd, addLabel }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
      <div>
        <h1 className="heading-lg">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{sub}</p>
      </div>
      {onAdd && (
        <button type="button" onClick={onAdd} className="btn-primary" data-testid="section-add">
          <Plus className="h-4 w-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}
